import { useEffect, useState } from "react";
import {
  EyeIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  EyeSlashIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import AdminNewsModal from "./AdminNewsModal";
import NewsArticleContent from "../../../pages/News/NewsArticleContent";
import { useAuth } from "../../../contexts/AuthContext";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import FormInput from "../../../components/controls/FormInput";
import Button from "../../../components/controls/Button";
import DataGrid from "../../../components/Datagrid";
import Pagination from "../../../components/controls/Pagination";

export default function AdminNewsPage() {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [news, setNews] = useState([]);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewItem, setPreviewItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [tags, setTags] = useState([]);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY + 25 });
  };

  useEffect(() => {
    if (userId) {
      loadTags();
      loadNews();
    }
  }, [page, search, companyFilter, userId]);

  const loadTags = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`,
      {
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return;

    const data = await res.json();

    setTags(
      data.map((t) => ({
        id: t.id,
        name: t.nameFr,
      }))
    );
  };

  const loadNews = async () => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/News/Admin?page=${page}&pageSize=20&q=${search}&company=${companyFilter}`;

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": userId,
      },
    });

    if (!res.ok) return;

    const data = await res.json();
    setNews(data.items);
    setTotalPages(data.totalPages);
  };

  const columns = [
    { key: "title", label: "Titre" },
    { key: "companyName", label: "Compagnie" },
    {
      key: "tag",
      label: "Tag",
      render: (row) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs border border-gray-200">
          {row.tagName || tags.find((t) => t.id === row.tagId)?.name || "—"}
        </span>
      ),
    },
    {
      key: "publishDate",
      label: "Date de publication",
      render: (row) => (
        <span className="text-gray-800">{formatDateTime(row.publishDate)}</span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => <span className="capitalize">{row.status}</span>,
    },
    {
      key: "isActive",
      label: "Actif",
      render: (row) =>
        row.isActive ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs">
            <EyeIcon className="w-4 h-4" />
            Actif
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 text-xs">
            <EyeSlashIcon className="w-4 h-4" />
            Inactif
          </span>
        ),
    },
    {
      key: "isHiddenByAdmin",
      label: "Actif par Admin",
      render: (row) =>
        row.isHiddenByAdmin ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 text-xs">
            <NoSymbolIcon className="w-4 h-4" />
            Caché
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs">
            <EyeIcon className="w-4 h-4" />
            Visible
          </span>
        ),
    },
    {
      key: "featured",
      label: "En vedette",
      render: (row) => (
        <div
          className="relative group flex justify-center"
          onMouseMove={handleMouseMove}
        >
          <FireIcon
            className={`w-6 h-6 ${
              row.isFeatured ? "text-orange-400" : "text-gray-400"
            }`}
          />
          {row.isFeatured && row.featuredStartAt && row.featuredEndAt && (
            <div
              className="fixed pointer-events-none z-[9999] bg-gray-900 text-white text-xs px-4 py-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
              style={{
                top: tooltipPos.y,
                left: tooltipPos.x,
                transform: "translate(-50%, 0)",
              }}
            >
              <div className="font-medium">
                Début : {formatDateTime(row.featuredStartAt)}
              </div>
              <div className="font-medium">
                Fin : {formatDateTime(row.featuredEndAt)}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            type="button"
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setPreviewItem(row)}
          >
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-800" />
          </button>
          <button
            type="button"
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setEditingItem(row)}
          >
            <PencilSquareIcon className="w-5 h-5 text-gray-800" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6 text-gray-900">
      <h1 className="text-3xl font-bold text-gray-900">
        Validation des actualités
      </h1>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-1/2">
          <FormInput
            label="Rechercher"
            name="search"
            value={search}
            placeholder="Rechercher un titre..."
            onChange={(_, v) => {
              setSearch(v);
              setPage(1);
            }}
            showRequiredOrOptional={false}
          />
        </div>

        <div className="w-full md:w-1/3">
          <FormInput
            label="Compagnie"
            name="companyFilter"
            value={companyFilter}
            placeholder="Filtrer par compagnie..."
            onChange={(_, v) => {
              setCompanyFilter(v);
              setPage(1);
            }}
            showRequiredOrOptional={false}
          />
        </div>

        <div className="flex justify-end w-full md:w-auto md:items-end">
          <Button
            as="button"
            type="button"
            color="primary"
            size="md"
            onClick={() =>
              setEditingItem({
                id: null,
                title: "",
                text: "",
                imageUrl: "",
                publishDate: null,
                tagId: "",
                companyId: "",
                isHiddenByAdmin: false,
                isFeatured: false,
                featuredStartAt: null,
                featuredEndAt: null,
              })
            }
            className="w-full md:w-auto"
          >
            Ajouter une actualité
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border rounded-xl shadow-sm bg-base-100">
        <DataGrid
          columns={columns}
          data={news}
          loading={false}
          emptyMessage="Aucune actualité trouvée."
          className="border rounded-xl shadow-sm bg-white"
        />
      </div>

      <Pagination
        page={page}
        totalPages={2}
        onChangePage={setPage}
        className="mt-4"
      />

      {previewItem && (
        <dialog className="modal modal-open modal-middle">
          <div className="modal-box max-w-4xl max-h-[85vh] overflow-y-auto p-0 bg-white">
            <div className="p-6">
              <NewsArticleContent article={previewItem} />
            </div>

            <div className="modal-action p-4 border-t bg-white">
              <button
                className="btn btn-outline rounded-full"
                onClick={() => setPreviewItem(null)}
              >
                Fermer
              </button>
            </div>
          </div>
        </dialog>
      )}

      {editingItem && (
        <AdminNewsModal
          item={editingItem}
          onClose={() => {
            setEditingItem(null);
            loadNews();
          }}
          onSaved={() => {
            setEditingItem(null);
            loadNews();
          }}
          userId={userId}
        />
      )}
    </div>
  );
}
